import emitMetric from '@rover/utilities/emitMetric';

import MediaFile from './MediaFile';
import getMediaDimensions from './mediaMetadata';
import { MediaType, UploadMediaOptions } from './types';

/**
 * Metrics for picking and uploading media, shared by web, iOS and Android.
 *
 * Each metric is a count with string tags. Numbers such as size or duration are
 * sent as range tags (for example `sizeBucket: '1-2mb'`) instead of raw values,
 * because native `emitMetric` only accepts tags. That keeps the same metric
 * comparable across all three platforms.
 *
 * Sending a metric never throws, so it can't break a selection or an upload.
 */

const UNKNOWN = 'unknown';

const KB = 1024;
const MB = 1024 * KB;

type Threshold = [ceiling: number, label: string];

function bucket(value: number | undefined, thresholds: Threshold[], overflow: string): string {
  if (value === undefined || !Number.isFinite(value) || value < 0) return UNKNOWN;
  const match = thresholds.find(([ceiling]) => value < ceiling);
  return match ? match[1] : overflow;
}

export function getSizeBucket(bytes: number | undefined): string {
  return bucket(
    bytes,
    [
      [100 * KB, '0-100kb'],
      [500 * KB, '100-500kb'],
      [MB, '500kb-1mb'],
      [2 * MB, '1-2mb'],
      [5 * MB, '2-5mb'],
      [10 * MB, '5-10mb'],
      [20 * MB, '10-20mb'],
    ],
    '20mb+'
  );
}

export function getMegapixelBucket(width?: number, height?: number): string {
  if (!width || !height) return UNKNOWN;
  return bucket(
    (width * height) / 1_000_000,
    [
      [1, '0-1mp'],
      [2, '1-2mp'],
      [5, '2-5mp'],
      [8, '5-8mp'],
      [12, '8-12mp'],
      [20, '12-20mp'],
    ],
    '20mp+'
  );
}

export function getLongEdgeBucket(width?: number, height?: number): string {
  if (!width || !height) return UNKNOWN;
  return bucket(
    Math.max(width, height),
    [
      [1025, '0-1024'],
      [2049, '1025-2048'],
      [3025, '2049-3024'],
      [4033, '3025-4032'],
      [6001, '4033-6000'],
    ],
    '6000+'
  );
}

/** Coarse orientation and shape, e.g. phone portrait vs screenshot vs panorama. */
export function getAspectRatioBucket(width?: number, height?: number): string {
  if (!width || !height) return UNKNOWN;
  const ratio = width / height;
  if (ratio < 0.5) return 'portrait-tall';
  if (ratio < 0.6) return 'portrait-9:16';
  if (ratio < 0.8) return 'portrait-3:4';
  if (ratio < 0.95) return 'portrait-other';
  if (ratio <= 1.05) return 'square';
  if (ratio < 1.4) return 'landscape-4:3';
  if (ratio < 1.9) return 'landscape-16:9';
  return 'landscape-wide';
}

export function getDurationBucket(seconds: number | undefined): string {
  return bucket(
    seconds,
    [
      [5, '0-5s'],
      [15, '5-15s'],
      [30, '15-30s'],
      [60, '30-60s'],
      [180, '1-3m'],
      [600, '3-10m'],
    ],
    '10m+'
  );
}

export function getElapsedBucket(milliseconds: number | undefined): string {
  return bucket(
    milliseconds,
    [
      [250, '0-250ms'],
      [500, '250-500ms'],
      [1_000, '500ms-1s'],
      [2_000, '1-2s'],
      [5_000, '2-5s'],
      [10_000, '5-10s'],
      [30_000, '10-30s'],
      [60_000, '30-60s'],
    ],
    '60s+'
  );
}

export function getCountBucket(count: number | undefined): string {
  if (count === undefined || !Number.isFinite(count) || count < 0) return UNKNOWN;
  if (count <= 5) return String(Math.trunc(count));
  return bucket(
    count,
    [
      [11, '6-10'],
      [21, '11-20'],
    ],
    '20+'
  );
}

/** Upload speed in bits per second. */
export function getThroughputBucket(bytesPerSecond: number | undefined): string {
  if (bytesPerSecond === undefined || !Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) {
    return UNKNOWN;
  }
  return bucket(
    bytesPerSecond * 8,
    [
      [50_000, '0-50kbps'],
      [250_000, '50-250kbps'],
      [1_000_000, '250kbps-1mbps'],
      [5_000_000, '1-5mbps'],
      [20_000_000, '5-20mbps'],
    ],
    '20mbps+'
  );
}

/** How much the client-side transcode shrank or grew the file. */
export function getSizeDeltaBucket(beforeBytes?: number, afterBytes?: number): string {
  if (!beforeBytes || afterBytes === undefined || afterBytes < 0) return UNKNOWN;
  const ratio = afterBytes / beforeBytes;
  if (ratio < 0.25) return 'shrank-75pct-plus';
  if (ratio < 0.5) return 'shrank-50-75pct';
  if (ratio < 0.75) return 'shrank-25-50pct';
  if (ratio < 0.98) return 'shrank-0-25pct';
  if (ratio <= 1.02) return 'unchanged';
  if (ratio < 1.25) return 'grew-0-25pct';
  if (ratio < 1.5) return 'grew-25-50pct';
  if (ratio < 2) return 'grew-50-100pct';
  return 'grew-100pct-plus';
}

/** UploadTask progress is a 0-100 percentage. */
export function getProgressBucket(percent: number | undefined): string {
  return bucket(
    percent,
    [
      [1, '0pct'],
      [25, '1-25pct'],
      [50, '25-50pct'],
      [75, '50-75pct'],
      [100, '75-99pct'],
    ],
    '100pct'
  );
}

/**
 * The file format, such as `jpeg` or `mov`. Uses the MIME type, or the file
 * extension when some Android pickers leave the MIME type empty.
 */
export function getFormatTag(mediaFile: Pick<MediaFile, 'name' | 'file'>): string {
  const mimeSubtype = (mediaFile.file?.type || '').split('/')[1]?.toLowerCase();
  const normalised = mimeSubtype === 'jpg' ? 'jpeg' : mimeSubtype;
  if (normalised && /^[a-z0-9.+-]{1,16}$/.test(normalised)) return normalised;

  const extension = mediaFile.name?.split('.').pop()?.toLowerCase();
  if (extension && /^[a-z0-9]{1,8}$/.test(extension)) {
    return extension === 'jpg' ? 'jpeg' : extension;
  }
  return UNKNOWN;
}

export function getMediaTypeTag(type: MediaType | undefined): string {
  if (type === MediaType.IMAGE) return 'image';
  if (type === MediaType.VIDEO) return 'video';
  return UNKNOWN;
}

/** Where the upload is going (`messages`, `pets`, `people`, ...), as sent by the client. */
export function getObjectTypeTag(options: UploadMediaOptions | undefined): string {
  if (!options) return UNKNOWN;
  if ('objectType' in options && options.objectType) return options.objectType;
  if ('contentUrl' in options && options.contentUrl) return 'content-url';
  return UNKNOWN;
}

/**
 * The error's type, such as `MediaUploadNetworkError`. Media errors carry it in
 * `errorType`; other errors fall back to `name`. `constructor.name` isn't used
 * because minification rewrites it in production builds.
 */
export function getErrorTypeTag(error: unknown): string {
  if (!(error instanceof Error)) return UNKNOWN;
  const { errorType } = error as { errorType?: unknown };
  if (typeof errorType === 'string' && errorType) return errorType;
  return error.name || UNKNOWN;
}

export const MediaMetricNames = {
  SELECTION_COMPLETED: 'media_selection_completed',
  SELECTION_REJECTED: 'media_selection_rejected',
  FILE_SELECTED: 'media_file_selected',
  FILE_DIMENSIONS: 'media_file_dimensions',
  VIDEO_SELECTED: 'media_video_selected',
  TRANSCODE_COMPLETED: 'media_transcode_completed',
  MAGICK_INIT: 'media_magick_init',
  UPLOAD_STARTED: 'media_upload_started',
  UPLOAD_SUCCEEDED: 'media_upload_succeeded',
  UPLOAD_FAILED: 'media_upload_failed',
  UPLOAD_RETRIED: 'media_upload_retried',
  UPLOAD_CANCELLED: 'media_upload_cancelled',
  ATTACH_FAILED: 'media_attach_failed',
  SEND_LATENCY: 'media_send_latency',
} as const;

export type MediaMetricName = (typeof MediaMetricNames)[keyof typeof MediaMetricNames];

/**
 * Sends one media metric and ignores any error. `emitMetric` adds the page tags
 * (concept, uiName), so these metrics can be split by page.
 */
export function emitMediaMetric(name: MediaMetricName, tags: Record<string, string> = {}): void {
  try {
    emitMetric(name, tags);
  } catch {
    // metrics are best-effort
  }
}

/**
 * Sends one metric for the whole selection, then one per file. Dimensions go on a
 * separate metric to keep the number of tag combinations small.
 *
 * Only uses values already in memory and never reads the file, so it adds no
 * noticeable time to a selection.
 */
export function emitSelectionMetrics(
  mediaFiles: MediaFile[],
  { selectionPath, transcoded }: { selectionPath: string; transcoded: boolean }
): void {
  const images = mediaFiles.filter((file) => file.type === MediaType.IMAGE);
  const videos = mediaFiles.filter((file) => file.type === MediaType.VIDEO);

  emitMediaMetric(MediaMetricNames.SELECTION_COMPLETED, {
    selectionPath,
    batchSize: getCountBucket(mediaFiles.length),
    imageCount: getCountBucket(images.length),
    videoCount: getCountBucket(videos.length),
  });

  mediaFiles.forEach((file) => {
    const mediaType = getMediaTypeTag(file.type);
    const { width, height, durationSeconds, original } = getMediaDimensions(file);
    const format = getFormatTag(original ?? file);
    const sizeBucket = getSizeBucket(original ? original.size : file.size);

    emitMediaMetric(MediaMetricNames.FILE_SELECTED, {
      selectionPath,
      mediaType,
      format,
      sizeBucket,
      transcoded: String(transcoded && file.type === MediaType.IMAGE),
    });

    // Web doesn't know the dimensions, so it skips this metric instead of sending `unknown`.
    if (width !== undefined || height !== undefined) {
      emitMediaMetric(MediaMetricNames.FILE_DIMENSIONS, {
        mediaType,
        megapixels: getMegapixelBucket(width, height),
        longEdge: getLongEdgeBucket(width, height),
        aspectRatio: getAspectRatioBucket(width, height),
      });
    }

    if (file.type === MediaType.VIDEO) {
      emitMediaMetric(MediaMetricNames.VIDEO_SELECTED, {
        format,
        sizeBucket,
        duration: getDurationBucket(durationSeconds),
        longEdge: getLongEdgeBucket(width, height),
        aspectRatio: getAspectRatioBucket(width, height),
      });
    }
  });
}

export function emitSelectionRejected(error: unknown, selectionPath: string): void {
  emitMediaMetric(MediaMetricNames.SELECTION_REJECTED, {
    selectionPath,
    errorType: getErrorTypeTag(error),
  });
}
